import { VOICES } from "./voices";

let ctx = null;
let master = null;
let reverbIn = null;
let noiseBuf = null;
let ambientOn = false;
const raw = {};
const decoded = {};

export function preloadVoices() {
  Object.entries(VOICES).forEach(([id, b64]) => {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    raw[id] = bytes.buffer;
  });
}

function impulse(sec, decay) {
  const len = Math.floor(ctx.sampleRate * sec);
  const b = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = b.getChannelData(ch);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
  }
  return b;
}

export function initAudio() {
  if (ctx) {
    ctx.resume();
    return;
  }
  ctx = new (window.AudioContext || window.webkitAudioContext)();
  const comp = ctx.createDynamicsCompressor();
  comp.threshold.value = -22;
  comp.knee.value = 12;
  comp.ratio.value = 4;
  master = ctx.createGain();
  master.gain.value = 0.65;
  master.connect(comp);
  comp.connect(ctx.destination);
  const conv = ctx.createConvolver();
  conv.buffer = impulse(2.5, 2.4);
  reverbIn = ctx.createGain();
  reverbIn.gain.value = 0.45;
  reverbIn.connect(conv);
  conv.connect(master);
  noiseBuf = ctx.createBuffer(1, ctx.sampleRate * 3, ctx.sampleRate);
  const nd = noiseBuf.getChannelData(0);
  for (let i = 0; i < nd.length; i++) nd[i] = Math.random() * 2 - 1;
}

function noise() {
  const s = ctx.createBufferSource();
  s.buffer = noiseBuf;
  s.loop = true;
  return s;
}

function out(node, wet = 0.4) {
  node.connect(master);
  const s = ctx.createGain();
  s.gain.value = wet;
  node.connect(s);
  s.connect(reverbIn);
}

function osc(type, freq) {
  const o = ctx.createOscillator();
  o.type = type;
  o.frequency.value = freq;
  return o;
}

function thump(vol) {
  const t = ctx.currentTime;
  const o = osc("sine", 70);
  const g = ctx.createGain();
  o.frequency.setValueAtTime(70, t);
  o.frequency.exponentialRampToValueAtTime(32, t + 0.35);
  g.gain.setValueAtTime(vol, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + 0.6);
  o.connect(g);
  out(g, 0.25);
  o.start(t);
  o.stop(t + 0.7);
}

export function startAmbient() {
  if (ambientOn) return;
  ambientOn = true;
  const t = ctx.currentTime;
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 170;
  lp.Q.value = 6;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(0.15, t + 4);
  [41.2, 82.4].forEach((f) => {
    const o = osc("sawtooth", f);
    o.connect(lp);
    o.start();
  });
  const lfo = osc("sine", 0.07);
  const lfoG = ctx.createGain();
  lfoG.gain.value = 70;
  lfo.connect(lfoG);
  lfoG.connect(lp.frequency);
  lfo.start();
  const n = noise();
  const nlp = ctx.createBiquadFilter();
  nlp.type = "lowpass";
  nlp.frequency.value = 110;
  const ng = ctx.createGain();
  ng.gain.value = 0.35;
  n.connect(nlp);
  nlp.connect(ng);
  ng.connect(g);
  n.start();
  lp.connect(g);
  out(g, 0.3);
  setInterval(() => thump(0.22), 2400);
}

export function beep(freq = 1400, count = 2) {
  const t = ctx.currentTime;
  for (let i = 0; i < count; i++) {
    const o = osc("square", freq + i * 300);
    const g = ctx.createGain();
    const s = t + i * 0.13;
    g.gain.setValueAtTime(0.0001, s);
    g.gain.linearRampToValueAtTime(0.08, s + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, s + 0.09);
    o.connect(g);
    out(g, 0.5);
    o.start(s);
    o.stop(s + 0.1);
  }
}

export function powerUp(dur = 1.4) {
  const t = ctx.currentTime;
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.setValueAtTime(200, t);
  lp.frequency.exponentialRampToValueAtTime(4000, t + dur);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.25, t + dur);
  g.gain.linearRampToValueAtTime(0, t + dur + 0.05);
  [30, 60].forEach((f) => {
    const o = osc("sawtooth", f);
    o.frequency.exponentialRampToValueAtTime(f * 12, t + dur);
    o.connect(lp);
    o.start(t);
    o.stop(t + dur + 0.1);
  });
  lp.connect(g);
  out(g, 0.5);
}

export function impact(vol = 1) {
  const t = ctx.currentTime;
  const sub = osc("sine", 160);
  const sg = ctx.createGain();
  sub.frequency.setValueAtTime(160, t);
  sub.frequency.exponentialRampToValueAtTime(24, t + 1.4);
  sg.gain.setValueAtTime(vol, t);
  sg.gain.exponentialRampToValueAtTime(0.001, t + 2.8);
  sub.connect(sg);
  out(sg, 0.5);
  sub.start(t);
  sub.stop(t + 3);

  const n = noise();
  const nlp = ctx.createBiquadFilter();
  nlp.type = "lowpass";
  nlp.frequency.setValueAtTime(2400, t);
  nlp.frequency.exponentialRampToValueAtTime(80, t + 1.6);
  const ng = ctx.createGain();
  ng.gain.setValueAtTime(vol * 0.9, t);
  ng.gain.exponentialRampToValueAtTime(0.001, t + 2);
  n.connect(nlp);
  nlp.connect(ng);
  out(ng, 0.8);
  n.start(t);
  n.stop(t + 2.1);

  [220, 487].forEach((f) => {
    const o = osc("sine", f);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.06 * vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 3.5);
    o.connect(g);
    out(g, 0.9);
    o.start(t);
    o.stop(t + 3.6);
  });
}

export function womp(vol = 1) {
  const t = ctx.currentTime;
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.Q.value = 14;
  lp.frequency.setValueAtTime(120, t);
  lp.frequency.exponentialRampToValueAtTime(1800, t + 0.12);
  lp.frequency.exponentialRampToValueAtTime(90, t + 1.1);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.4 * vol, t + 0.06);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 1.4);
  [55, 27.5].forEach((f) => {
    const o = osc("sawtooth", f);
    o.frequency.setValueAtTime(f * 1.6, t);
    o.frequency.exponentialRampToValueAtTime(f * 0.8, t + 1.2);
    o.connect(lp);
    o.start(t);
    o.stop(t + 1.5);
  });
  lp.connect(g);
  out(g, 0.6);
  thump(0.8 * vol);
}

function whoosh(t, f, len, pan, vol) {
  const n = noise();
  const bp = ctx.createBiquadFilter();
  bp.type = "bandpass";
  bp.Q.value = 1.4;
  bp.frequency.setValueAtTime(f * 0.4, t);
  bp.frequency.exponentialRampToValueAtTime(f * 2.6, t + len);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(vol, t + len * 0.65);
  g.gain.exponentialRampToValueAtTime(0.0001, t + len);
  const p = ctx.createStereoPanner();
  p.pan.setValueAtTime(-pan, t);
  p.pan.linearRampToValueAtTime(pan, t + len);
  n.connect(bp);
  bp.connect(g);
  g.connect(p);
  out(p, 0.55);
  n.start(t, Math.random() * 2);
  n.stop(t + len + 0.05);
}

export function uploadSequence(dur) {
  const t0 = ctx.currentTime;
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.Q.value = 8;
  lp.frequency.setValueAtTime(300, t0);
  lp.frequency.exponentialRampToValueAtTime(7000, t0 + dur);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(0.1, t0 + dur);
  g.gain.linearRampToValueAtTime(0, t0 + dur + 0.08);
  [70, 105].forEach((f) => {
    const o = osc("sawtooth", f);
    o.frequency.exponentialRampToValueAtTime(f * 16, t0 + dur);
    o.connect(lp);
    o.start(t0);
    o.stop(t0 + dur + 0.1);
  });
  lp.connect(g);
  out(g, 0.5);

  let t = 0;
  let i = 0;
  while (t < dur - 0.25) {
    const p = t / dur;
    whoosh(t0 + t, 250 + p * p * 3800, 0.95 - p * 0.55, i % 2 ? 0.9 : -0.9, 0.25 + p * 0.25);
    t += 0.95 - p * 0.55;
    i++;
  }

  for (let k = 0; k < dur * 4; k++) {
    const s = t0 + Math.random() * dur;
    const o = osc("square", 1500 + Math.random() * 3000);
    const bg = ctx.createGain();
    bg.gain.setValueAtTime(0.025, s);
    bg.gain.exponentialRampToValueAtTime(0.0001, s + 0.04);
    o.connect(bg);
    out(bg, 0.3);
    o.start(s);
    o.stop(s + 0.05);
  }
}

export function successChord() {
  const t = ctx.currentTime;
  [130.8, 392, 523.3, 784].forEach((f, i) => {
    ["sine"].forEach((type, j) => {
      const o = osc(type, f * (j ? 1.003 : 1));
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(0.06, t + 0.6 + i * 0.12);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 7);
      o.connect(g);
      out(g, 0.9);
      o.start(t);
      o.stop(t + 7.1);
    });
  });
}

function speakFallback(text) {
  return new Promise((res) => {
    if (!window.speechSynthesis) return res();
    const u = new SpeechSynthesisUtterance(text);
    u.pitch = 0.2;
    u.rate = 0.8;
    u.onend = res;
    u.onerror = res;
    window.speechSynthesis.speak(u);
    setTimeout(res, 6000);
  });
}

export async function speak(id, fallbackText) {
  if (!decoded[id] && raw[id]) {
    try {
      decoded[id] = await ctx.decodeAudioData(raw[id].slice(0));
    } catch (e) {
      console.error("decode failed", e);
    }
  }
  const buf = decoded[id];
  if (!buf) return speakFallback(fallbackText);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.playbackRate.value = 0.94;
  const bus = ctx.createGain();
  bus.gain.value = 1.5;
  src.connect(bus);

  const rm = ctx.createGain();
  rm.gain.value = 0;
  const carrier = osc("sine", 52);
  carrier.connect(rm.gain);
  src.connect(rm);
  const rmw = ctx.createGain();
  rmw.gain.value = 0.55;
  rm.connect(rmw);
  rmw.connect(bus);

  const d = ctx.createDelay();
  d.delayTime.value = 0.009;
  const fb = ctx.createGain();
  fb.gain.value = 0.55;
  src.connect(d);
  d.connect(fb);
  fb.connect(d);
  const dw = ctx.createGain();
  dw.gain.value = 0.35;
  d.connect(dw);
  dw.connect(bus);

  out(bus, 0.45);
  carrier.start();
  src.start();
  return new Promise((res) => {
    src.onended = () => {
      carrier.stop();
      res();
    };
  });
}
